import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

const InteractiveBreathing = ({ onComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale, hold, exhale
  const [count, setCount] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [targetCycles, setTargetCycles] = useState(5);
  const [breathingRate, setBreathingRate] = useState([4]); // seconds
  const [showMotivation, setShowMotivation] = useState(false);

  const motivationalMessages = [
    "You're doing amazing! 🌟",
    "Feel the calm flowing through you 🌊",
    "Your breath is your superpower 💨",
    "You're stronger with every breath 💪",
    "Peace is growing within you 🕊️",
    "You've got this! Keep going 🚀",
    "Your mind is becoming clearer ✨",
    "You're creating your own calm space 🧘‍♀️"
  ];

  const [currentMessage, setCurrentMessage] = useState(motivationalMessages[0]);

  useEffect(() => {
    let timer;
    if (isActive) {
      const phaseLength = breathingRate[0] * 1000;
      
      timer = setTimeout(() => {
        if (phase === 'inhale') {
          setPhase('hold');
        } else if (phase === 'hold') {
          setPhase('exhale');
        } else if (phase === 'exhale') {
          setPhase('inhale');
          setCycles(prev => {
            const newCycles = prev + 1;
            if (newCycles >= targetCycles) {
              setIsActive(false);
              setPhase('complete');
              if (onComplete) onComplete({ type: 'breathing', cycles: newCycles });
            }
            return newCycles;
          });
          // Show motivational message every 2 cycles
          if ((cycles + 1) % 2 === 0) {
            const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
            setCurrentMessage(randomMessage);
            setShowMotivation(true);
            setTimeout(() => setShowMotivation(false), 2000);
          }
        }
      }, phase === 'hold' ? phaseLength / 2 : phaseLength);
    }

    return () => clearTimeout(timer);
  }, [isActive, phase, cycles, targetCycles, breathingRate]);

  const startBreathing = () => {
    setIsActive(true);
    setPhase('inhale');
    setCycles(0);
    setCount(0);
  };

  const stopBreathing = () => {
    setIsActive(false);
    setPhase('ready');
  };

  const resetBreathing = () => {
    setIsActive(false);
    setPhase('ready');
    setCycles(0);
  };

  const getCircleSize = () => {
    switch (phase) {
      case 'inhale': return 'w-64 h-64';
      case 'hold': return 'w-64 h-64';
      case 'exhale': return 'w-32 h-32';
      default: return 'w-48 h-48';
    }
  };

  const getCircleColor = () => {
    switch (phase) {
      case 'inhale': return 'from-sky-400 via-blue-400 to-purple-400';
      case 'hold': return 'from-green-400 via-emerald-400 to-teal-400';
      case 'exhale': return 'from-pink-400 via-rose-400 to-red-400';
      case 'complete': return 'from-yellow-400 via-orange-400 to-amber-400';
      default: return 'from-gray-300 to-gray-400';
    }
  };

  const getInstructions = () => {
    switch (phase) {
      case 'inhale': return `Breathe In... ${breathingRate[0]}s`;
      case 'hold': return `Hold... ${breathingRate[0] / 2}s`;
      case 'exhale': return `Breathe Out... ${breathingRate[0]}s`;
      case 'complete': return '🎉 Amazing work!';
      default: return 'Ready to start your breathing journey?';
    }
  };

  if (phase === 'complete') {
    return (
      <div className="text-center space-y-8" data-testid="breathing-complete">
        <div className="relative">
          <div className={`mx-auto rounded-full bg-gradient-to-r ${getCircleColor()} flex items-center justify-center transition-all duration-1000 ${getCircleSize()}`}>
            <div className="text-6xl">✨</div>
          </div>
          <div className="absolute -inset-4 rounded-full border-4 border-yellow-300 animate-ping opacity-50"></div>
        </div>
        
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-gray-900">
            Breathing Session Complete! 🌟
          </h3>
          <p className="text-lg text-gray-600">
            You completed {cycles} breathing cycles. How do you feel?
          </p>
          
          <div className="glass-card p-6 bg-gradient-to-r from-blue-50 to-amber-50 max-w-md mx-auto">
            <p className="text-emerald-800 font-medium mb-3">
              🧠 <strong>What just happened:</strong>
            </p>
            <ul className="text-sm text-blue-700 space-y-1 text-left">
              <li>• Your nervous system is now more calm</li>
              <li>• Your heart rate has likely decreased</li>
              <li>• You've strengthened your focus muscle</li>
              <li>• You created a moment of peace</li>
            </ul>
          </div>
        </div>
        
        <div className="flex space-x-4 justify-center">
          <Button onClick={resetBreathing} className="btn-primary">
            🔄 Practice Again
          </Button>
          <Button onClick={() => window.history.back()} variant="outline" className="btn-secondary">
            ✅ I'm Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-8" data-testid="interactive-breathing">
      {/* Settings */}
      {!isActive && phase === 'ready' && (
        <div className="max-w-md mx-auto space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Customize Your Session 🎛️</h2>
          
          <div className="glass-card p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Breathing Pace: {breathingRate[0]} seconds
              </label>
              <Slider
                value={breathingRate}
                onValueChange={setBreathingRate}
                max={8}
                min={3}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>Fast (3s)</span>
                <span>Medium (5s)</span>
                <span>Slow (8s)</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Number of Cycles: {targetCycles}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[3, 5, 10].map(num => (
                  <Button
                    key={num}
                    onClick={() => setTargetCycles(num)}
                    variant={targetCycles === num ? "default" : "outline"}
                    className={targetCycles === num ? "btn-primary" : "btn-secondary"}
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breathing Circle */}
      <div className="relative flex items-center justify-center h-80">
        {/* Background rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-80 h-80 rounded-full border-2 border-gray-200 opacity-20"></div>
          <div className="absolute w-96 h-96 rounded-full border border-gray-100 opacity-10"></div>
        </div>
        
        {/* Main breathing circle */}
        <div className={`rounded-full bg-gradient-to-r ${getCircleColor()} flex items-center justify-center transition-all duration-[${breathingRate[0] * 1000}ms] ease-in-out ${getCircleSize()} shadow-2xl`}>
          <div className="text-white font-bold text-xl">
            {isActive ? cycles + 1 : '🫁'}
          </div>
        </div>
        
        {/* Floating particles */}
        {isActive && (
          <>
            <div className="absolute top-10 left-10 w-3 h-3 bg-cyan-300 rounded-full animate-bounce opacity-70" style={{animationDelay: '0s'}}></div>
            <div className="absolute top-20 right-16 w-2 h-2 bg-blue-300 rounded-full animate-bounce opacity-70" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute bottom-16 left-20 w-4 h-4 bg-purple-300 rounded-full animate-bounce opacity-70" style={{animationDelay: '1s'}}></div>
            <div className="absolute bottom-10 right-12 w-2 h-2 bg-pink-300 rounded-full animate-bounce opacity-70" style={{animationDelay: '1.5s'}}></div>
          </>
        )}
      </div>

      {/* Instructions */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">
          {getInstructions()}
        </h3>
        
        {isActive && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              Cycle {cycles + 1} of {targetCycles}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 max-w-xs mx-auto">
              <div 
                className="bg-gradient-to-r from-sky-400 to-cyan-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((cycles) / targetCycles) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Motivational Message */}
      {showMotivation && (
        <div className="glass-card p-4 bg-gradient-to-r from-yellow-50 to-orange-50 max-w-sm mx-auto animate-pulse">
          <p className="text-yellow-800 font-medium">{currentMessage}</p>
        </div>
      )}

      {/* Controls */}
      <div className="flex space-x-4 justify-center">
        {!isActive && phase === 'ready' ? (
          <Button onClick={startBreathing} className="btn-primary px-8 py-4 text-lg">
            🌬️ Start Breathing
          </Button>
        ) : isActive ? (
          <>
            <Button onClick={stopBreathing} variant="outline" className="btn-secondary">
              ⏸️ Pause
            </Button>
            <Button onClick={resetBreathing} variant="outline" className="btn-secondary">
              🔄 Reset
            </Button>
          </>
        ) : null}
      </div>
      
      {/* Tips */}
      {!isActive && phase === 'ready' && (
        <div className="max-w-lg mx-auto">
          <div className="glass-card p-6 bg-gradient-to-r from-blue-50 to-cyan-50">
            <h4 className="font-semibold text-blue-800 mb-3">💡 Pro Tips for Youth</h4>
            <ul className="text-sm text-blue-700 space-y-2 text-left">
              <li>• Find a comfy spot where you won't be interrupted</li>
              <li>• It's totally normal if your mind wanders - just come back to your breath</li>
              <li>• Start with shorter sessions and work your way up</li>
              <li>• Use this when you're stressed, before tests, or just need a reset</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveBreathing;
